import React, { useState, useEffect, useRef } from 'react'
import { Card, Row, Col, Statistic, Progress, Button, Typography, Space, Badge, message, Form, Input, Select, Radio, Divider, List, Avatar, Tag, Tooltip, Alert, Spin, Modal, Tabs } from 'antd'
import { motion } from 'framer-motion'
import { 
  UserOutlined, 
  TrophyOutlined,
  FireOutlined, 
  StarOutlined,
  CrownOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  SettingOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  HeartOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  LikeOutlined,
  DislikeOutlined,
  CommentOutlined,
  MessageOutlined,
  BellOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  LockOutlined,
  UnlockOutlined,
  KeyOutlined,
  SafetyOutlined,
  SecurityScanOutlined,
  BugOutlined,
  ToolOutlined,
  ExperimentOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/api'
import { GlobalStyles, COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css'
import { on as onEvent } from '@/services/events'
import { couponService, CouponInfo } from '@/services/couponService'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

interface Achievement {
  id: string
  name: string
  description: string
  icon: string
  unlocked: boolean
  progress?: number
  target?: number
  unlockedAt?: string
}

interface Stats {
  totalGenerations: number
  videoGenerations: number
  imageGenerations: number
  totalCreditsUsed: number
  totalCreditsRecharged: number
  currentCredits: number
  completionRate: number
  videoCompletedCount: number
  imageCompletedCount: number
  memberDays: number
  memberSince: Date | null
  achievements: Achievement[]
}

interface CreditPackage {
  id: string
  name: string
  credits: number
  price: number
  currency: string
  bonus: number
  popular: boolean
  isDefault: boolean
  enabled: boolean
  description?: string
}

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  currency: string
  period: string
  credits: number
  features: string[]
  popular: boolean
  isDefault: boolean
  enabled: boolean
  description?: string
  trialDays?: number
}

interface PaymentMethod {
  id: string
  name: string
  enabled: boolean
  isDefault: boolean
  icon: string
  description: string
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(false)

  const [statsLoading, setStatsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [stats, setStats] = useState<Stats>({
    totalGenerations: 0,
    videoGenerations: 0,
    imageGenerations: 0,
    totalCreditsUsed: 0,
    totalCreditsRecharged: 0,
    currentCredits: 0,
    completionRate: 0,
    videoCompletedCount: 0,
    imageCompletedCount: 0,
    memberDays: 0,
    memberSince: null,
    achievements: []
  })
  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [packages, setPackages] = useState<CreditPackage[]>([])
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<string>('wechat')
  const [userTouchedSelection, setUserTouchedSelection] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'credits' | 'subscription'>('credits')
  const [messageApi, contextHolder] = message.useMessage()
  const [couponCode, setCouponCode] = useState('')
  const [couponInfo, setCouponInfo] = useState<any>(null)
  const [verifyingCoupon, setVerifyingCoupon] = useState(false)
  const [usingCoupon, setUsingCoupon] = useState(false)
  
  // 二维码弹窗状态（用于聚合支付PC端）
  const [qrCodeVisible, setQrCodeVisible] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [currentOrderId, setCurrentOrderId] = useState('')
  const [polling, setPolling] = useState(false)
  const pollingIntervalRef = useRef<number | null>(null)

  // 默认积分包数据
  const defaultCreditPackages: CreditPackage[] = [
    {
      id: 'basic',
      name: '入门包',
      credits: 100,
      price: 9.99,
      currency: 'CNY',
      bonus: 0,
      popular: false,
      isDefault: false,
      enabled: true,
      description: '适合新手用户'
    },
    {
      id: 'standard',
      name: '标准包',
      credits: 500,
      price: 39.99,
      currency: 'CNY',
      bonus: 50,
      popular: true,
      isDefault: true,
      enabled: true,
      description: '性价比最高'
    },
    {
      id: 'premium',
      name: '高级包',
      credits: 1000,
      price: 69.99,
      currency: 'CNY',
      bonus: 150,
      popular: false,
      isDefault: false,
      enabled: true,
      description: '适合重度用户'
    }
  ]

  // 默认订阅计划数据
  const defaultSubscriptionPlans: SubscriptionPlan[] = [
    {
      id: 'basic',
      name: '基础会员',
      price: 19.99,
      currency: 'CNY',
      period: 'monthly',
      credits: 200,
      features: ['每月200积分', '优先支持', '无广告'],
      popular: false,
      isDefault: false,
      enabled: true,
      description: '适合轻度用户'
    },
    {
      id: 'pro',
      name: 'Pro会员',
      price: 49.99,
      currency: 'CNY',
      period: 'monthly',
      credits: 600,
      features: ['每月600积分', '优先支持', '无广告', '高级功能'],
      popular: true,
      isDefault: true,
      enabled: true,
      description: '最受欢迎的选择'
    }
  ]

  // 推荐项（isDefault > popular > 第一项）
  const getRecommendedPackage = () => {
    const list = (packages.length > 0 ? packages : defaultCreditPackages) as CreditPackage[]
    return list.find(p => (p as any).isDefault) || list.find(p => (p as any).popular) || list[0]
  }
  const getRecommendedPlan = () => {
    const list = (subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans) as SubscriptionPlan[]
    return list.find(p => (p as any).isDefault) || list.find(p => (p as any).popular) || list[0]
  }

  // 加载统计数据
  const loadStats = async () => {
    if (!user) return

    try {
      setStatsLoading(true)
      console.log('📊 [个人中心] 开始加载统计数据...')
      const res = await apiClient.get('/user/stats')
      
      console.log('✅ [个人中心] 统计数据响应:', res.data)
      const data = res.data.data || res.data
      console.log('📊 [个人中心] 解析后的数据:', data)
      
      setStats({
        totalGenerations: data.totalGenerations || 0,
        videoGenerations: data.videoGenerations || 0,
        imageGenerations: data.imageGenerations || 0,
        totalCreditsUsed: data.totalCreditsUsed || 0,
        totalCreditsRecharged: data.totalCreditsRecharged || 0,
        currentCredits: data.currentCredits || user.pointsBalance || 0,
        completionRate: data.completionRate || 0,
        videoCompletedCount: data.videoCompletedCount || 0,
        imageCompletedCount: data.imageCompletedCount || 0,
        memberDays: data.memberDays || 0,
        memberSince: data.memberSince ? new Date(data.memberSince) : null,
        achievements: data.achievements || []
      })

      if (data.achievements && Array.isArray(data.achievements)) {
        setAchievements(data.achievements)
        console.log(`✅ [个人中心] 成就加载完成: ${data.achievements.filter((a: any) => a.unlocked).length}/${data.achievements.length}`)
      }
      
      console.log('✅ [个人中心] 统计数据加载完成')
    } catch (error) {
      console.error('❌ [个人中心] 加载统计数据失败:', error)
      messageApi.error('加载统计数据失败，请刷新页面重试')
    } finally {
      setStatsLoading(false)
    }
  }

  // 加载支付数据
  const loadPaymentData = async () => {
    try {
      setPaymentLoading(true)
      console.log('🔄 开始加载支付数据...')
      
      // 并行请求所有数据，增加超时时间
      const requests = [
        apiClient.get('/payment/credit-packages', { timeout: 10000 }),
        apiClient.get('/payment/subscription-plans', { timeout: 10000 }),
        apiClient.get('/payment/methods', { timeout: 10000 })
      ]
      
      const [packagesRes, plansRes, methodsRes] = await Promise.allSettled(requests)
      
      // 处理积分包数据
      let finalPackages: CreditPackage[] = []
      if (packagesRes.status === 'fulfilled') {
        console.log('📦 积分包API响应:', packagesRes.value.data)
        const packagesData = packagesRes.value.data.data?.packages || packagesRes.value.data.packages || []
        const enabledPackages = packagesData.filter((pkg: any) => pkg.enabled !== false)
        console.log('📦 启用的积分包:', enabledPackages)
        setPackages(enabledPackages)
        finalPackages = enabledPackages.length > 0 ? enabledPackages : defaultCreditPackages
      } else {
        const errorInfo = packagesRes.status === 'rejected' ? packagesRes.reason : (packagesRes as any).value?.data
        console.warn('📦 积分包API失败，使用默认数据', errorInfo)
        console.warn('📦 积分包API完整错误:', packagesRes)
        setPackages(defaultCreditPackages)
        finalPackages = defaultCreditPackages
      }
      
      // 处理订阅计划数据
      let finalPlans: SubscriptionPlan[] = []
      if (plansRes.status === 'fulfilled') {
        console.log('🎖️ 订阅套餐API响应:', plansRes.value.data)
        const plansData = plansRes.value.data.data?.plans || plansRes.value.data.plans || []
        const enabledPlans = plansData.filter((plan: any) => plan.enabled !== false)
        console.log('🎖️ 启用的订阅套餐:', enabledPlans)
        setSubscriptionPlans(enabledPlans)
        finalPlans = enabledPlans.length > 0 ? enabledPlans : defaultSubscriptionPlans
      } else {
        const errorInfo = plansRes.status === 'rejected' ? plansRes.reason : (plansRes as any).value?.data
        console.warn('🎖️ 订阅套餐API失败，使用默认数据', errorInfo)
        console.warn('🎖️ 订阅套餐API完整错误:', plansRes)
        setSubscriptionPlans(defaultSubscriptionPlans)
        finalPlans = defaultSubscriptionPlans
      }
      
      // 处理支付方式数据（仅使用启用的方式，并设置默认方式）
      if (methodsRes.status === 'fulfilled') {
        console.log('💳 支付方式API响应:', methodsRes.value.data)
        const raw = methodsRes.value.data?.data?.methods ?? methodsRes.value.data?.methods ?? methodsRes.value.data ?? []
        const methodsData = (Array.isArray(raw) ? raw : [])
          .filter((m: any) => m && m.enabled !== false)
        console.log('💳 启用的支付方式:', methodsData)
        setPaymentMethods(methodsData)
        const def = methodsData.find((m: any) => m.isDefault) || methodsData[0]
        if (def?.id) setPaymentMethod(def.id)
      } else {
        const errorInfo = methodsRes.status === 'rejected' ? methodsRes.reason : (methodsRes as any).value?.data
        console.warn('💳 支付方式API失败，不再使用兜底数据', errorInfo)
        console.warn('💳 支付方式API完整错误:', methodsRes)
        setPaymentMethods([])
      }
      
      // 默认选中推荐项的逻辑（使用局部变量 finalPackages，而不是状态中的 packages）
      // 查找推荐的积分包（优先级：isDefault > popular > 第一项）
      const recommendedPackage = finalPackages.find(pkg => pkg.isDefault) || 
                                 finalPackages.find(pkg => pkg.popular) || 
                                 finalPackages[0]
      
      // 查找推荐的订阅计划（优先级：isDefault > popular > 第一项）
      const recommendedPlan = finalPlans.find(plan => plan.isDefault) || 
                             finalPlans.find(plan => plan.popular) || 
                             finalPlans[0]
      
      // 决定默认显示哪个 Tab 和选中哪个项（优先积分包）
      if (recommendedPackage) {
        // 如果积分包有推荐项，默认选中积分包
        setActiveTab('credits')
        setSelectedPackage(recommendedPackage.id)
        setSelectedPlan(null)
        console.log('✅ 默认选中积分包:', recommendedPackage.name)
      } else if (recommendedPlan) {
        // 如果积分包无推荐项但订阅计划有，默认选中订阅计划
        setActiveTab('subscription')
        setSelectedPlan(recommendedPlan.id)
        setSelectedPackage(null)
        console.log('✅ 默认选中订阅计划:', recommendedPlan.name)
      }
      
      console.log('✅ 支付数据加载完成')
    } catch (error) {
      console.error('❌ 获取支付配置失败:', error)
      messageApi.warning('支付配置加载失败，请稍后重试')
      setPackages(defaultCreditPackages)
      setSubscriptionPlans(defaultSubscriptionPlans)
      setPaymentMethods([])
    } finally {
      setPaymentLoading(false)
    }
  }

  // 支付状态轮询（用于PC端Native支付和移动端H5支付）
  const startPollingPaymentStatus = (orderId: string) => {
    // 清理之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }
    
    setPolling(true)
    setCurrentOrderId(orderId) // 保存订单ID，用于手动检查
    let retryCount = 0
    const maxRetries = 3
    let pollCount = 0
    const maxPollCount = 30 // 最多轮询30次（60秒）
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        pollCount++
        console.log(`🔍 [ProfilePage] 轮询支付状态 (${pollCount}/${maxPollCount}):`, orderId)
        
        const res = await apiClient.get(`/payment/status/${orderId}`)
        if (res.success && res.data?.status === 'COMPLETED') {
          // 支付成功
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          setPolling(false)
          setQrCodeVisible(false)
          setCurrentOrderId('')
          
          // 清除localStorage中的待支付订单
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
          
          messageApi.success('支付成功！')
          
          // 🔥 修复：立即刷新用户信息（包括积分），然后刷新统计数据
          const { refreshUser } = useAuthStore.getState()
          try {
            await refreshUser()
            console.log('✅ [ProfilePage] 用户信息已刷新，积分已更新')
          } catch (error) {
            console.error('❌ [ProfilePage] 刷新用户信息失败:', error)
          }
          
          // 延迟刷新统计数据和支付历史，确保后端处理完成
          setTimeout(() => {
            loadStats()
            loadPaymentData()
            // 再次刷新用户信息，确保积分同步
            refreshUser().catch(err => {
              console.warn('⚠️ [ProfilePage] 二次刷新用户信息失败:', err)
            })
          }, 500)
        } else if (res.success && res.data?.status === 'FAILED') {
          // 支付失败
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          setPolling(false)
          setCurrentOrderId('')
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
          messageApi.error('支付失败，请重试')
        }
        retryCount = 0 // 重置重试计数
      } catch (error) {
        console.error('轮询支付状态失败:', error)
        retryCount++
        if (retryCount >= maxRetries) {
          console.warn(`轮询失败达到最大重试次数 ${maxRetries}，暂停一次轮询`)
          retryCount = 0
        }
      }
      
      // 达到最大轮询次数，停止轮询
      if (pollCount >= maxPollCount) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setPolling(false)
        messageApi.info('自动检查已停止，您可以手动检查支付状态')
      }
    }, 2000) // 每2秒轮询一次
  }

  // 手动检查支付状态
  const manualCheckPaymentStatus = async (orderId: string) => {
    try {
      setPolling(true)
      const res = await apiClient.get(`/payment/status/${orderId}`)
      if (res.success && res.data?.status === 'COMPLETED') {
        // 支付成功
        setPolling(false)
        setQrCodeVisible(false)
        messageApi.success('支付成功！')
        
        // 刷新用户信息和统计数据
        const { refreshToken } = useAuthStore.getState()
        await refreshToken()
        setTimeout(() => {
          loadStats()
          loadPaymentData()
        }, 500)
      } else {
        setPolling(false)
        messageApi.info('支付尚未完成，请继续等待或重新扫码')
      }
    } catch (error) {
      setPolling(false)
      console.error('手动检查支付状态失败:', error)
      messageApi.error('检查支付状态失败，请稍后重试')
    }
  }
  
  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])
  
  // 处理支付URL，根据协议类型选择打开方式
  const handlePaymentUrl = (paymentData: any) => {
    console.log('🔍 [ProfilePage] handlePaymentUrl 调用:', {
      paymentMethod,
      paymentData,
      hasQrCode: !!paymentData.qrCode,
      hasPaymentUrl: !!paymentData.paymentUrl,
      hasH5Url: !!paymentData.h5Url,
      hasJsapiParams: !!paymentData.jsapiParams
    })
    
    // 聚合支付特殊处理（PC端显示二维码，移动端跳转）
    if (paymentMethod === 'xunhu') {
      const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      const deviceType = paymentData.deviceType || (isMobile ? 'mobile' : 'pc')
      
      console.log('🔍 [ProfilePage] 聚合支付检测:', {
        isMobile,
        deviceType,
        qrCode: paymentData.qrCode,
        paymentUrl: paymentData.paymentUrl
      })
      
      if (deviceType === 'pc' && paymentData.qrCode) {
        // PC端显示二维码
        console.log('✅ [ProfilePage] PC端显示二维码:', paymentData.qrCode)
        setQrCodeUrl(paymentData.qrCode)
        setCurrentOrderId(paymentData.orderId)
        setQrCodeVisible(true)
        startPollingPaymentStatus(paymentData.orderId)
      } else {
        // 移动端跳转支付URL（优化：在新窗口打开并开始轮询）
        const url = paymentData.paymentUrl || paymentData.payUrl
        console.log('📱 [ProfilePage] 移动端聚合支付跳转:', url)
        console.log('📱 [ProfilePage] 订单ID:', paymentData.orderId)
        
        if (url) {
          // 保存订单ID到localStorage，用于支付返回后检测
          if (paymentData.orderId) {
            localStorage.setItem('pendingPaymentOrderId', paymentData.orderId)
            localStorage.setItem('pendingPaymentTime', Date.now().toString())
            console.log('💾 [ProfilePage] 已保存订单ID到localStorage:', paymentData.orderId)
            
            // 开始轮询支付状态
            console.log('🔄 [ProfilePage] 开始轮询聚合支付状态...')
            startPollingPaymentStatus(paymentData.orderId)
          }
          
          // 在新窗口打开支付页面（这样用户支付完成后可以返回原页面）
          const paymentWindow = window.open(url, '_blank')
          
          // 如果新窗口被阻止，则直接跳转
          if (!paymentWindow) {
            console.warn('⚠️ [ProfilePage] 无法打开新窗口，直接跳转')
            window.location.href = url
          }
        }
      }
      return
    }
    
    // 处理JSAPI支付（微信内支付）
    if (paymentData.jsapiParams) {
      // 需要在微信环境中调起微信支付SDK
      const invokePay = () => {
        if (typeof window !== 'undefined' && (window as any).WeixinJSBridge) {
          (window as any).WeixinJSBridge.invoke(
            'getBrandWCPayRequest',
            paymentData.jsapiParams,
            (res: any) => {
              if (res.err_msg === 'get_brand_wcpay_request:ok') {
                messageApi.success('支付成功')
                // 刷新用户信息和统计数据
                const { refreshToken } = useAuthStore.getState()
                refreshToken().then(() => {
                  setTimeout(() => {
                    loadStats()
                    loadPaymentData()
                  }, 500)
                })
              } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
                messageApi.info('支付已取消')
              } else {
                messageApi.error('支付失败：' + res.err_msg)
              }
            }
          )
        } else {
          messageApi.warning('请在微信内打开页面完成支付')
        }
      }

      // 如果WeixinJSBridge已加载，直接调用；否则等待加载
      if (typeof window !== 'undefined' && (window as any).WeixinJSBridge) {
        invokePay()
      } else if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // 等待WeixinJSBridge加载
        document.addEventListener('WeixinJSBridgeReady', invokePay, false)
        // 如果等待2秒后仍未加载，提示用户
        setTimeout(() => {
          if (!(window as any).WeixinJSBridge) {
            messageApi.warning('请在微信内打开页面完成支付')
          }
        }, 2000)
      } else {
        messageApi.warning('请在微信内打开页面完成支付')
      }
      return
    }

    // 处理H5支付（移动浏览器）
    if (paymentData.h5Url) {
      console.log('📱 [ProfilePage] H5支付跳转:', paymentData.h5Url)
      console.log('📱 [ProfilePage] 订单ID:', paymentData.orderId)
      
      // 保存订单ID到localStorage，用于支付返回后检测
      if (paymentData.orderId) {
        localStorage.setItem('pendingPaymentOrderId', paymentData.orderId)
        localStorage.setItem('pendingPaymentTime', Date.now().toString())
        console.log('💾 [ProfilePage] 已保存订单ID到localStorage:', paymentData.orderId)
        
        // 开始轮询支付状态（参考PC端实现）
        console.log('🔄 [ProfilePage] 开始轮询H5支付状态...')
        startPollingPaymentStatus(paymentData.orderId)
      }
      
      // 在新窗口打开微信支付（这样用户支付完成后可以返回原页面）
      const paymentWindow = window.open(paymentData.h5Url, '_blank')
      
      // 如果新窗口被阻止，则直接跳转
      if (!paymentWindow) {
        console.warn('⚠️ [ProfilePage] 无法打开新窗口，直接跳转')
        window.location.href = paymentData.h5Url
      }
      
      return
    }

    // 处理Native支付（扫码支付）或通用支付URL
    const url = paymentData.paymentUrl || paymentData.payUrl
    if (url) {
      // 检测设备类型
      const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      
      // 判断URL协议类型
      if (url.startsWith('weixin://')) {
        if (!isMobile && paymentMethod === 'wechat') {
          // PC端微信支付：显示二维码
          console.log('✅ [ProfilePage] PC端微信支付显示二维码:', url)
          
          // 使用在线QR码生成服务
          const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(url)}`
          setQrCodeUrl(qrCodeImageUrl)
          setCurrentOrderId(paymentData.orderId)
          setQrCodeVisible(true)
          startPollingPaymentStatus(paymentData.orderId)
        } else {
          // 移动端：直接跳转打开微信
          window.location.href = url
        }
      } else if (url.startsWith('http://') || url.startsWith('https://')) {
        // HTTP/HTTPS链接，在新窗口打开
        window.open(url, '_blank')
      } else {
        // 其他协议，尝试直接跳转
        window.location.href = url
      }
    }
  }

  // 验证优惠券
  const handleVerifyCoupon = async () => {
    if (!couponCode.trim()) {
      messageApi.warning('请输入优惠券代码')
      return
    }

    setVerifyingCoupon(true)
    try {
      const info = await couponService.verifyCoupon(couponCode.trim())
      setCouponInfo(info)
      messageApi.success(`优惠券有效！将获得 ${info.credits} 积分`)
    } catch (error: any) {
      setCouponInfo(null)
      messageApi.error(error.response?.data?.message || error.message || '优惠券验证失败')
    } finally {
      setVerifyingCoupon(false)
    }
  }

  // 使用优惠券充值
  const handleUseCoupon = async () => {
    if (!couponCode.trim()) {
      messageApi.warning('请输入优惠券代码')
      return
    }

    if (!couponInfo) {
      messageApi.warning('请先验证优惠券')
      return
    }

    setUsingCoupon(true)
    try {
      const result = await couponService.useCoupon(couponCode.trim())
      messageApi.success(`优惠券使用成功！获得 ${result.credits} 积分，当前余额：${result.newBalance}`)
      setCouponCode('')
      setCouponInfo(null)
      // 刷新用户信息
      const { refreshToken } = useAuthStore.getState()
      await refreshToken()
      // 刷新统计数据
      setTimeout(() => {
        loadStats()
        loadPaymentData()
      }, 500)
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || error.message || '优惠券使用失败')
    } finally {
      setUsingCoupon(false)
    }
  }

  // 购买积分
  const handlePurchaseCredits = async () => {
    if (!selectedPackage) {
      messageApi.error('请选择积分包')
      return
    }
    
    try {
      setLoading(true)
      const allPkgs = packages.length > 0 ? packages : defaultCreditPackages
      const pkg = allPkgs.find((p) => p.id === selectedPackage)
      const isUsingDefaultPackage = packages.length === 0
      
      // 积分包购买：不传 planId，直接传 amount 和 credits
      // 因为积分包可能来自配置（payment_packages），不在 Plan 表中
      // 只有订阅计划（SUBSCRIPTION）才需要传 planId
      const requestBody: any = {
        amount: Number(pkg?.price) || 0,
        credits: Number(pkg?.credits) || 0,
        paymentMethod,
        type: 'CREDITS',
        scene: 'normal',
        couponCode: couponCode.trim() || undefined
        // 注意：积分包不传 planId，避免后端在 Plan 表中查找失败
      }
      
      const res = await apiClient.post('/payment/create-order', requestBody)

      // 如果使用优惠券成功（返回isCoupon标识），直接返回
      if (res.success && res.data?.isCoupon) {
        messageApi.success(`优惠券使用成功！获得 ${res.data.credits} 积分`)
        setCouponCode('')
        setCouponInfo(null)
        // 🔥 修复：立即刷新用户信息（包括积分）
        const { refreshUser } = useAuthStore.getState()
        try {
          await refreshUser()
          console.log('✅ [ProfilePage] 优惠券使用成功，用户信息已刷新')
        } catch (error) {
          console.error('❌ [ProfilePage] 刷新用户信息失败:', error)
        }
        setTimeout(() => {
          loadStats()
          loadPaymentData()
          // 再次刷新用户信息，确保积分同步
          refreshUser().catch(err => {
            console.warn('⚠️ [ProfilePage] 二次刷新用户信息失败:', err)
          })
        }, 500)
        return
      }

      if (res.success) {
        console.log('✅ [ProfilePage] 订单创建成功:', res.data)
        messageApi.success('支付订单已创建，请完成支付')
        // 使用新的支付URL处理函数
        handlePaymentUrl(res.data)
        // 刷新支付历史
        setTimeout(() => loadPaymentData(), 500)
      } else {
        console.error('❌ [ProfilePage] 订单创建失败:', res.message)
        messageApi.error(res.message || '创建支付订单失败')
      }
    } catch (error: any) {
      console.error('购买积分失败:', error)
      messageApi.error(error?.message || '购买积分失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 订阅会员
  const handleSubscribe = async () => {
    if (!selectedPlan) {
      messageApi.error('请选择订阅计划')
      return
    }
    
    try {
      setLoading(true)
      const allPlans = subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans
      const plan = allPlans.find((p) => p.id === selectedPlan)
      const isUsingDefaultPlan = subscriptionPlans.length === 0
      
      // 订阅套餐购买：不传 planId，直接传 amount 和 credits
      // 因为订阅套餐可能来自配置（payment_plans），不在 Plan 表中
      // 只有数据库中的 Plan 记录才需要传 planId
      const requestBody: any = {
        amount: Number((plan as any)?.price) || 0,
        credits: Number((plan as any)?.credits) || 0,
        paymentMethod,
        type: 'SUBSCRIPTION',
        scene: 'normal'
        // 注意：订阅套餐不传 planId，避免后端在 Plan 表中查找失败
      }
      
      const res = await apiClient.post('/payment/create-order', requestBody)

      if (res.success) {
        console.log('✅ [ProfilePage] 订阅订单创建成功:', res.data)
        messageApi.success('订阅订单创建成功')
        // 使用新的支付URL处理函数
        handlePaymentUrl(res.data)
        // 刷新支付历史
        setTimeout(() => loadPaymentData(), 500)
      } else {
        console.error('❌ [ProfilePage] 订阅订单创建失败:', res.message)
        messageApi.error(res.message || '创建订阅订单失败')
      }
    } catch (error) {
      console.error('订阅失败:', error)
      messageApi.error('订阅失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 选择积分包
  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId)
    setSelectedPlan(null) // 取消选择订阅计划
    setActiveTab('credits') // 自动切换到积分包标签
    setUserTouchedSelection(true)
  }

  // 选择订阅计划
  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId)
    setSelectedPackage(null) // 取消选择积分包
    setActiveTab('subscription') // 自动切换到订阅套餐标签
    setUserTouchedSelection(true)
  }

  // 加载用户统计数据
  useEffect(() => {
    if (user) {
      console.log('👤 用户状态:', user)
      console.log('✅ 用户已登录，开始加载数据')
      loadStats()
    } else {
      console.log('❌ 用户未登录，无法加载统计数据')
    }
  }, [user])

  // 支付数据加载（不需要用户登录）
  useEffect(() => {
    loadPaymentData()
  }, [])

  // 页面加载时检测是否有待支付的订单（H5支付返回后）
  useEffect(() => {
    const checkPendingPayment = async () => {
      const pendingOrderId = localStorage.getItem('pendingPaymentOrderId')
      const pendingTime = localStorage.getItem('pendingPaymentTime')
      
      if (pendingOrderId && pendingTime) {
        const timeDiff = Date.now() - parseInt(pendingTime, 10)
        // 如果订单在5分钟内，开始轮询
        if (timeDiff < 5 * 60 * 1000) {
          console.log('🔍 [ProfilePage] 检测到待支付订单，开始轮询:', pendingOrderId)
          startPollingPaymentStatus(pendingOrderId)
          messageApi.info('检测到未完成的支付，正在检查支付状态...')
        } else {
          // 超过5分钟，清除localStorage
          console.log('⏰ [ProfilePage] 待支付订单已过期，清除记录')
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
        }
      }
    }
    
    checkPendingPayment()
  }, [])

  // 监听配置更新事件
  useEffect(() => {
    const handleConfigUpdate = () => {
      console.log('🔄 [个人中心] 检测到支付配置更新，重新加载数据')
      loadPaymentData()
    }
    const off = onEvent('payment_config_updated', handleConfigUpdate) as unknown as (() => void) | undefined
    return () => {
      try { typeof off === 'function' && off() } catch {}
    }
  }, [])

  return (
    <div className="profile-page min-h-screen w-full relative">
      {contextHolder}
      
      {/* 移动端背景装饰 */}
      <div className="md:hidden absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-100/50 to-transparent pointer-events-none profile-mobile-hero"></div>
      <div className="unified-container relative z-10" style={{
        paddingTop: 'var(--spacing-sm)'
      }}>
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full profile-header-section">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-lg)',
                gap: 'var(--spacing-md)'
              }}
            >
          {/* 左侧：标题和副标题 */}
          <div className="flex-1">
            <div className="flex items-center" style={{
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <span style={{ fontSize: 'var(--font-h1-mobile)' }}>👤</span>
              <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                个人中心
              </h1>
            </div>
            <Paragraph className="unified-text-secondary text-base md:ml-12 !mb-0">
              管理您的账户信息和AI创作记录
            </Paragraph>
          </div>
          
          {/* 右侧：特色标签 */}
          <div className="flex flex-wrap gap-2 md:justify-end">
            <Tag icon={<UserOutlined />} className="unified-tag bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700">
              账户管理
            </Tag>
            <Tag icon={<TrophyOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
              创作统计
            </Tag>
            <Tag icon={<CreditCardOutlined />} className="unified-tag bg-gradient-to-r from-pink-50 to-pink-100 text-pink-700">
              积分记录
            </Tag>
          </div>
        </motion.div>
        </div>

        <Row gutter={[0, 16]}>
          {/* 用户信息和统计：全宽与下方充值中心一致 */}
          <Col xs={24} lg={24}>
            {statsLoading && (
              <Card
                title="📈 个人概览"
                className="unified-card"
                style={{ marginBottom: 'var(--spacing-lg)' }}
                styles={{ body: { minHeight: 180 } }}
              >
                <div className="text-center py-8">
                  <Spin size="large" />
                  <div className="mt-4 text-gray-600">正在加载统计数据...</div>
                </div>
              </Card>
            )}
            {/* 用户信息卡片 */}
              <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="unified-card" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div className="flex items-center" style={{
                  gap: 'var(--spacing-lg)',
                  marginBottom: 'var(--spacing-lg)'
                }}>
                  <Avatar size={64} icon={<UserOutlined />} />
                  <div className="flex-1">
                    <Title level={3} className="mb-1">
                      {user?.username || user?.email || '用户'}
                  </Title>
                    <Text className="text-gray-600">
                      {user?.email}
                    </Text>
                    <div className="mt-2 flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <CrownOutlined />
                        <Text className="text-gray-600">
                          {user?.role === 'ADMIN' ? '管理员' : '普通用户'}
                        </Text>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CalendarOutlined />
                        <Text className="text-gray-600">
                          注册 {stats.memberDays} 天
                        </Text>
                      </div>
                    </div>
                    </div>
                  </div>

                <Row gutter={[0, 12]} className="md:!mx-[-8px]">
                  <Col xs={12} sm={12} md={6} lg={6} className="md:!px-2">
                    <Statistic
                      title={<span className="text-gray-600 text-sm font-medium">当前积分</span>}
                      value={user?.pointsBalance || 0}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6} className="md:!px-2">
                    <Statistic
                      title={<span className="text-gray-600 text-sm font-medium">已用积分</span>}
                      value={stats.totalCreditsUsed || 0}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6} className="md:!px-2">
                    <Statistic
                      title={<span className="text-gray-600 text-sm font-medium">视频生成</span>}
                      value={stats.videoGenerations || 0}
                      valueStyle={{ color: '#1677ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6} className="md:!px-2">
                    <Statistic
                      title={<span className="text-gray-600 text-sm font-medium">图片生成</span>}
                      value={stats.imageGenerations || 0}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Card>
                    </motion.div>
                    
            {/* 创作统计已合并到上方卡片 */}
                    
            {/* 成就系统（按需求移除） */}
          </Col>

          {/* 充值中心：移动到下方，并在所有屏幕占满一行 */}
          <Col xs={24}>
              <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              >
            <Card 
                title="💎 充值中心" 
                className="unified-card"
                style={{ marginBottom: 'var(--spacing-xl)' }}
                styles={{ body: { minHeight: 180 } }}
                extra={
                  <Button 
                    type="link" 
                    icon={<HistoryOutlined />}
                    onClick={() => navigate('/payment')}
                  >
                    查看详情
                  </Button>
                }
              >
                {paymentLoading && (
                  <div className="text-center py-8">
                    <Spin size="large" />
                    <div className="mt-4 text-gray-600">正在加载支付配置...</div>
                  </div>
                )}
                <div className="text-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <Text className="text-gray-600">
                    购买积分包或订阅会员，享受更多AI创作功能
                  </Text>
                </div>

                {/* 标签页切换：积分包和订阅套餐 */}
                <Tabs 
                  size="large"
                  animated
                  centered
                  activeKey={activeTab}
                  onChange={(key) => {
                    const k = key as 'credits' | 'subscription'
                    setActiveTab(k)
                    if (!userTouchedSelection) {
                      if (k === 'credits' && !selectedPackage) {
                        const rec = getRecommendedPackage()
                        if (rec?.id) {
                          setSelectedPackage(rec.id)
                          setSelectedPlan(null)
                        }
                      } else if (k === 'subscription' && !selectedPlan) {
                        const rec = getRecommendedPlan()
                        if (rec?.id) {
                          setSelectedPlan(rec.id)
                          setSelectedPackage(null)
                        }
                      }
                    }
                  }}
                  items={[
                    {
                      key: 'credits',
                      label: (
                        <span className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                          <span>💎</span>
                          <span className="font-medium">积分包</span>
                        </span>
                      ),
                      children: (
                        <div style={{
                          marginTop: 'var(--spacing-lg)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--spacing-lg)'
                        }}>
                    {(packages.length > 0 ? packages : defaultCreditPackages).map((pkg) => (
                      <div
                        key={pkg.id}
                        className="border cursor-pointer transition-all unified-card"
                        style={{
                          padding: 'var(--spacing-md)',
                          borderRadius: 'var(--radius-lg)',
                          borderColor: selectedPackage === pkg.id ? '#60a5fa' : '#e5e7eb',
                          backgroundColor: selectedPackage === pkg.id ? '#eff6ff' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPackage !== pkg.id) {
                            e.currentTarget.style.borderColor = '#d1d5db'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPackage !== pkg.id) {
                            e.currentTarget.style.borderColor = '#e5e7eb'
                          }
                        }}
                        onClick={() => handlePackageSelect(pkg.id)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                              <div className="font-semibold">{pkg.name}</div>
                              {pkg.isDefault && (
                                <Tag color="blue">推荐</Tag>
                              )}
                              {pkg.popular && !pkg.isDefault && (
                                <Tag color="red">热门</Tag>
                              )}
                            </div>
                            <div className="text-gray-500" style={{
                              fontSize: 'var(--font-small)',
                              marginTop: 'var(--spacing-xs)'
                            }}>
                              {pkg.credits}积分
                              {pkg.bonus > 0 && ` + ${pkg.bonus}赠送`}
                            </div>
                            {pkg.description && (
                              <div className="text-gray-400" style={{
                                fontSize: 'var(--font-small)',
                                marginTop: 'var(--spacing-xs)'
                              }}>
                                {pkg.description}
                              </div>
                            )}
                          </div>
                          <div className="text-right" style={{ marginLeft: 'var(--spacing-lg)' }}>
                            <div className="font-bold text-blue-600" style={{
                              fontSize: 'var(--font-body)'
                            }}>
                              ￥{pkg.price}
                            </div>
                            {pkg.bonus > 0 && (
                              <div className="text-green-600 font-semibold" style={{
                                fontSize: 'var(--font-small)',
                                marginTop: 'var(--spacing-xs)'
                              }}>
                                +{pkg.bonus}赠送
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                  <Divider />

                  {/* 优惠券输入 */}
                  <div className="coupon-card bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200" style={{
                    padding: 'var(--spacing-lg)',
                    borderRadius: 'var(--radius-lg)'
                  }}>
                    <Text className="text-sm font-medium text-gray-700 mb-3 block">
                      🎟️ 使用优惠券
                    </Text>
                    <div className="flex w-full gap-2 mb-2 coupon-input-row">
                      <Input
                        placeholder="请输入优惠券代码"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase())
                          if (couponInfo) setCouponInfo(null)
                        }}
                        className="h-10 flex-1"
                        onPressEnter={handleVerifyCoupon}
                      />
                      <Button
                        onClick={handleVerifyCoupon}
                        loading={verifyingCoupon}
                        className="h-10 flex-shrink-0 unified-button-secondary"
                      >
                        验证
                      </Button>
                      {couponInfo && (
                        <Button
                          type="primary"
                          onClick={handleUseCoupon}
                          loading={usingCoupon}
                          className="h-10 flex-shrink-0 unified-button-secondary"
                        >
                          使用
                        </Button>
                      )}
                    </div>
                    {couponInfo && (
                      <Alert
                        message={`优惠券有效！将获得 ${couponInfo.credits} 积分`}
                        description={couponInfo.description || couponInfo.name}
                        type="success"
                        showIcon
                        style={{ marginTop: 'var(--spacing-sm)' }}
                        closable
                        onClose={() => setCouponInfo(null)}
                      />
                    )}
                  </div>
                      </div>
                      )
                    },
                    {
                      key: 'subscription',
                      label: (
                        <span className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                          <span>🎖️</span>
                          <span className="font-medium">订阅套餐</span>
                        </span>
                      ),
                      children: (
                        <div style={{
                          marginTop: 'var(--spacing-lg)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--spacing-md)'
                        }}>
                    {(subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans).map((plan) => (
                      <div
                        key={plan.id}
                        className={`border rounded-lg cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-purple-400 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{
                          padding: 'var(--spacing-md)',
                          borderRadius: 'var(--radius-lg)'
                        }}
                        onClick={() => handlePlanSelect(plan.id)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                              <div className="font-semibold">{plan.name}</div>
                              {plan.isDefault && (
                                <Tag color="purple">推荐</Tag>
                              )}
                              {plan.popular && !plan.isDefault && (
                                <Tag color="red">热门</Tag>
                              )}
                            </div>
                            <div className="text-gray-500" style={{
                              fontSize: 'var(--font-small)',
                              marginTop: 'var(--spacing-xs)'
                            }}>
                              {plan.credits}积分/月
                            </div>
                            {plan.description && (
                              <div className="text-gray-400" style={{
                                fontSize: 'var(--font-small)',
                                marginTop: 'var(--spacing-xs)'
                              }}>
                                {plan.description}
                              </div>
                            )}
                            {plan.features && plan.features.length > 0 && (
                              <div className="text-gray-500" style={{
                                fontSize: 'var(--font-small)',
                                marginTop: 'var(--spacing-sm)'
                              }}>
                                {plan.features.slice(0, 2).join(' · ')}
                              </div>
                            )}
                          </div>
                          <div className="text-right" style={{ marginLeft: 'var(--spacing-lg)' }}>
                            <div className="font-bold text-purple-600" style={{
                              fontSize: 'var(--font-body)'
                            }}>
                              ￥{plan.price}
                              {plan.period === 'monthly' ? '/月' : 
                               plan.period === 'quarterly' ? '/季' : 
                               plan.period === 'yearly' ? '/年' : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                  <Divider />

                  {/* 订阅说明 */}
                  <div>
                    <Title level={5} className="unified-title-h5" style={{ marginBottom: 'var(--spacing-lg)' }}>订阅说明</Title>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--spacing-sm)'
                    }}>
                      <div className="text-gray-600" style={{ fontSize: 'var(--font-small)' }}>• 订阅后立即生效，每月自动续费</div>
                      <div className="text-gray-600" style={{ fontSize: 'var(--font-small)' }}>• 未使用的积分不会累积到下月</div>
                      <div className="text-gray-600" style={{ fontSize: 'var(--font-small)' }}>• 可随时取消订阅，取消后下月生效</div>
                      <div className="text-gray-600" style={{ fontSize: 'var(--font-small)' }}>• 订阅用户享受优先客服支持</div>
                    </div>
                  </div>
                      </div>
                      )
                    }
                  ]}
                />

                {/* 支付方式（移到 Tabs 外部） */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  style={{ marginTop: 'var(--spacing-xl)' }}
                >
                  <Card className="border border-gray-100" style={{ borderRadius: 'var(--radius-lg)' }}>
                    <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                      <Title level={5} className="unified-title-h5" style={{ marginBottom: 'var(--spacing-lg)' }}>💳 支付方式</Title>
                      <div className="flex flex-wrap" style={{ gap: 'var(--spacing-md)' }}>
                        {paymentMethods.map((method) => {
                          const displayName = method.id === 'xunhu' ? '微信聚合支付' : (method.name || method.id)
                          return (
                            <div 
                              key={method.id}
                              className="flex-1 min-w-[120px] border-2 cursor-pointer transition-all text-center unified-card"
                              style={{
                                padding: 'var(--spacing-md)',
                                borderRadius: 'var(--radius-lg)',
                                borderColor: paymentMethod === method.id 
                                  ? (method.id === 'wechat' ? '#22c55e' : 
                                     method.id === 'alipay' ? '#3b82f6' : 
                                     method.id === 'stripe' ? '#6366f1' : '#a78bfa')
                                  : '#e5e7eb',
                                backgroundColor: paymentMethod === method.id 
                                  ? (method.id === 'wechat' ? '#f0fdf4' : 
                                     method.id === 'alipay' ? '#eff6ff' : 
                                     method.id === 'stripe' ? '#eef2ff' : '#faf5ff')
                                  : 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (paymentMethod !== method.id) {
                                  e.currentTarget.style.borderColor = '#d1d5db'
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (paymentMethod !== method.id) {
                                  e.currentTarget.style.borderColor = '#e5e7eb'
                                }
                              }}
                              onClick={() => setPaymentMethod(method.id)}
                            >
                              <div className="font-semibold">{displayName}</div>
                              <div className="text-gray-500" style={{
                                fontSize: 'var(--font-small)',
                                marginTop: 'var(--spacing-xs)'
                              }}>{method.description || ''}</div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* 统一支付按钮 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  style={{ marginTop: 'var(--spacing-xl)' }}
                >
                  <Card className="border border-gray-100" style={{ borderRadius: 'var(--radius-lg)' }}>
                    <div>
                      <Title level={4} className="unified-title-h4" style={{ marginBottom: 'var(--spacing-lg)' }}>
                        确认支付
                        <span className="align-middle text-gray-600 font-normal" style={{
                          marginLeft: 'var(--spacing-md)',
                          fontSize: 'var(--font-small)'
                        }}>
                          {selectedPackage
                            ? `💳 积分包: ${((packages.length > 0 ? packages : defaultCreditPackages) as any[]).find((p: any) => p.id === selectedPackage)?.name || ''}`
                            : selectedPlan
                            ? `👑 订阅套餐: ${((subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans) as any[]).find((p: any) => p.id === selectedPlan)?.name || ''}`
                            : ''}
                        </span>
                      </Title>

                      <Button
                        type="primary"
                        size="large"
                        block
                        onClick={selectedPackage ? handlePurchaseCredits : handleSubscribe}
                        disabled={!selectedPackage && !selectedPlan}
                        loading={loading}
                        className="h-14 font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                        style={{
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none'
                        }}
                      >
                        {selectedPackage
                          ? (() => {
                              const list = (packages.length > 0 ? packages : defaultCreditPackages) as any[]
                              const price = list.find(p => p.id === selectedPackage)?.price
                              return (
                                <span>
                                  💳 立即购买积分 <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>￥{price ?? ''}</span>
                                </span>
                              )
                            })()
                          : selectedPlan
                          ? (() => {
                              const list = (subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans) as any[]
                              const price = list.find(p => p.id === selectedPlan)?.price
                              return (
                                <span>
                                  👑 立即订阅 <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>￥{price ?? ''}</span>
                                </span>
                              )
                            })()
                          : '请选择支付项目'}
                      </Button>
                    </div>
                  </Card>
                </motion.div>
            </Card>
          </motion.div>
          </Col>
        </Row>
        </motion.div>
      </div>
      
      {/* 聚合支付PC端二维码弹窗 */}
      <Modal
        title="微信扫码支付"
        open={qrCodeVisible}
        onCancel={() => {
          setQrCodeVisible(false)
          setPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }}
        footer={null}
        width={400}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {qrCodeUrl ? (
            <>
              {/* 使用图片显示二维码（聚合支付返回的 qrCode 是图片 URL） */}
              <div style={{ marginBottom: '8px' }}>
                <Text strong>支付二维码</Text>
              </div>
              <img 
                src={qrCodeUrl} 
                alt="支付二维码" 
                style={{ 
                  width: 256, 
                  height: 256, 
                  margin: '0 auto', 
                  display: 'block', 
                  border: '1px solid #ddd', 
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('❌ [ProfilePage] 二维码图片加载失败:', qrCodeUrl)
                  messageApi.error('二维码加载失败，请刷新页面重试')
                }}
                onLoad={() => {
                  console.log('✅ [ProfilePage] 二维码图片加载成功:', qrCodeUrl)
                }}
              />
              <div style={{ marginTop: '20px' }}>
                <Text type="secondary">请使用手机微信扫描二维码完成支付</Text>
              </div>
              {polling ? (
                <div style={{ marginTop: '10px' }}>
                  <Spin size="small" /> <Text type="secondary">等待支付完成...</Text>
                </div>
              ) : (
                <div style={{ marginTop: '15px' }}>
                  <Button 
                    type="primary" 
                    onClick={() => {
                      if (currentOrderId) {
                        manualCheckPaymentStatus(currentOrderId)
                      } else {
                        messageApi.warning('无法获取订单信息，请刷新页面重试')
                      }
                    }}
                    loading={polling}
                    style={{ marginRight: '10px' }}
                  >
                    手动检查支付状态
                  </Button>
                  <Button 
                    onClick={() => {
                      setQrCodeVisible(false)
                      setPolling(false)
                      if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current)
                        pollingIntervalRef.current = null
                      }
                    }}
                  >
                    关闭
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Spin size="large" />
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ProfilePage
